import classNames from 'classnames';
import { CheckMark, IconColors } from 'Icons';
import React from 'react';

import styles from './checkbox-card.module.css';

interface CheckboxCardProps {
    label: string;
    /** Optional second line under the label. */
    hint?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Shown on the right, outside the clickable area, e.g. a badge or a secondary action. */
    aside?: React.ReactNode;
    /** data-qa of the card. */
    dataQa?: string;
    className?: string;
}

/**
 * A checkbox inside a bordered card, highlighted while checked: the Compliance Builder's frequency cards, made
 * reusable. The whole left part (box, label, hint) toggles it; `aside` stays independent.
 */
function CheckboxCard({
    label,
    hint,
    checked,
    onChange,
    disabled = false,
    aside,
    dataQa,
    className,
}: CheckboxCardProps): React.ReactElement {
    const labelId = React.useId();
    const hintId = React.useId();

    return (
        <div
            className={classNames(styles.Card, className, {
                [styles.CardChecked]: checked,
                [styles.CardDisabled]: disabled,
            })}
            data-qa={dataQa}
        >
            <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-labelledby={labelId}
                aria-describedby={hint ? hintId : undefined}
                className={styles.Toggle}
                onClick={() => onChange(!checked)}
                disabled={disabled}
                data-qa={dataQa ? `${dataQa}-checkbox` : undefined}
            >
                <span className={styles.Checkbox} aria-hidden>
                    {checked && <CheckMark color={IconColors.White} aspect="0.875rem" />}
                </span>
                <span className={styles.Copy}>
                    <span className={styles.Label} id={labelId}>
                        {label}
                    </span>
                    {hint && (
                        <span className={styles.Hint} id={hintId}>
                            {hint}
                        </span>
                    )}
                </span>
            </button>

            {aside}
        </div>
    );
}

export default CheckboxCard;
